// Chemin: C:\gestion-copro\src\components\vigie\OnboardingSentinelleVigie.tsx
// Onboarding conversationnel SENTINELLE VIGIE — "Bonjour Nicole, vous n'êtes plus seule"
// Session 108 : Chantier 0 — Dialogue guidé, zéro quota IA, 5 phases
// Session 109 : Corrections — tantièmes (numérateur/dénominateur), statut occupation,
//               adresse postale, localisation malfaçons, création interlocuteurs auto
// Session 159 : Ajout étape infos_copropriete (CP, commune, nb lots, RNC)
//               Séparation adresse copro vs adresse copropriétaire

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { ChevronRight, Check, Upload, Plus, Trash2, Calendar, ArrowRight, HelpCircle, MapPin, Building2, Hash } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import {
  OnboardingStepId, OnboardingData, OnboardingMessage, OnboardingLot, NatureLot,
  RelationSyndic, EtatCS, GarantiesVEFA, StatutOccupation, LocalisationMalfacons,
  AdressePostale, InfosCopropriete, STEP_TO_PHASE, PHASE_LABELS, OnboardingPhase,
} from '../../types/onboarding.types';
import {
  saveOnboardingProgress, completeOnboarding, calculerGaranties,
  joursRestantsGarantie, formatDateGarantie,
} from '../../services/onboarding.service';
import SentinelleAvatarSVG from '../chat/SentinelleAvatar';

// ============================================
// PROPS
// ============================================

interface OnboardingSentinelleVigieProps {
  initialStep?: OnboardingStepId;
  initialData?: Partial<OnboardingData>;
  onComplete: () => void;
  onSkip: () => void;
}

// ============================================
// AVATAR
// ============================================

const SentinelleAvatar: React.FC<{ isThinking?: boolean; size?: 'sm' | 'md' | 'lg' | 'xl' }> = ({ isThinking, size = 'sm' }) => (
  <div className="flex-shrink-0"><SentinelleAvatarSVG size={size} isThinking={isThinking} /></div>
);

// ============================================
// ÉCRAN D'ACCUEIL PHASE 1
// ============================================

interface AccueilScreenProps { prenom: string; onStart: () => void; onSkip: () => void; }

const AccueilScreen: React.FC<AccueilScreenProps> = ({ prenom, onStart, onSkip }) => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timers = [setTimeout(() => setStep(1), 400), setTimeout(() => setStep(2), 1200), setTimeout(() => setStep(3), 2200), setTimeout(() => setStep(4), 3500), setTimeout(() => setStep(5), 4800)];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[500px] px-6 py-10 bg-gradient-to-b from-blue-50 via-white to-amber-50/30 rounded-xl border border-blue-100">
      <div className={`transition-all duration-700 ease-out ${step >= 1 ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-75 translate-y-4'}`}>
        <div className="relative">
          <div className="absolute inset-0 -m-4 bg-blue-400/10 rounded-full animate-pulse" />
          <div className="absolute inset-0 -m-8 bg-blue-300/5 rounded-full animate-pulse" style={{ animationDelay: '500ms' }} />
          <SentinelleAvatar size="xl" isThinking={false} />
        </div>
      </div>
      <div className={`mt-8 text-center transition-all duration-600 ease-out ${step >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h2 className="text-2xl font-bold text-gray-900">Bonjour {prenom} !</h2>
        <p className="text-lg text-blue-600 font-medium mt-1">Je suis SENTINELLE, votre copilote juridique.</p>
      </div>
      <div className={`mt-6 max-w-lg text-center transition-all duration-600 ease-out ${step >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <p className="text-gray-600 leading-relaxed">Mon rôle est simple : <span className="font-semibold text-gray-800">vous protéger</span>. Je connais le droit de la copropriété, les délais légaux, les pièges à éviter. Je suis disponible <span className="font-semibold text-gray-800">24h/24</span> et je n'ai qu'un seul objectif : défendre vos intérêts.</p>
      </div>
      <div className={`mt-5 transition-all duration-600 ease-out ${step >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="bg-blue-600 text-white px-6 py-3 rounded-xl shadow-lg shadow-blue-200 max-w-md text-center">
          <p className="text-sm font-medium leading-relaxed">Personne dans votre copropriété n'a ce rôle. Ni le syndic, ni le conseil syndical, ni le promoteur.<span className="block mt-1 text-base font-bold">Moi si.</span></p>
        </div>
      </div>
      <div className={`mt-8 w-full max-w-md space-y-3 transition-all duration-600 ease-out ${step >= 5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <p className="text-center text-sm text-gray-500 mb-4">Pour bien vous protéger, j'ai besoin de comprendre votre situation. C'est rapide, <span className="font-medium">environ 12 minutes</span>.</p>
        <button onClick={onStart} className="w-full bg-blue-600 text-white rounded-xl px-6 py-4 text-base font-semibold hover:bg-blue-700 transition-all hover:shadow-lg hover:shadow-blue-200 flex items-center justify-center gap-2 group">
          C'est parti, je vous fais confiance<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
        <button onClick={onSkip} className="w-full text-gray-500 rounded-xl px-6 py-3 text-sm hover:bg-gray-100 transition-colors">Plus tard, je veux explorer d'abord</button>
      </div>
    </div>
  );
};

// ============================================
// BULLE DE MESSAGE
// ============================================

const MessageBubble: React.FC<{ message: OnboardingMessage; isLatest: boolean }> = ({ message, isLatest }) => {
  const isSentinelle = message.role === 'sentinelle';
  return (
    <div className={`flex gap-3 ${isSentinelle ? '' : 'justify-end'} ${isLatest ? 'animate-fade-in' : ''}`}>
      {isSentinelle && <SentinelleAvatar isThinking={false} />}
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${isSentinelle ? 'bg-white border border-gray-200 text-gray-800 shadow-sm' : 'bg-blue-600 text-white'}`}>
        <div className="text-sm leading-relaxed whitespace-pre-line">{message.contenu}</div>
      </div>
    </div>
  );
};

// ============================================
// TYPING INDICATOR
// ============================================

const TypingIndicator: React.FC = () => (
  <div className="flex gap-3">
    <SentinelleAvatar isThinking />
    <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-1.5 shadow-sm">
      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  </div>
);

// ============================================
// BARRE DE PROGRESSION
// ============================================

const ProgressBar: React.FC<{ currentPhase: OnboardingPhase }> = ({ currentPhase }) => {
  const phases: OnboardingPhase[] = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-1 px-4 py-3 bg-white border-b border-gray-100">
      {phases.map((phase) => (
        <div key={phase} className="flex-1 flex flex-col items-center gap-1">
          <div className={`h-1.5 w-full rounded-full transition-colors duration-300 ${phase < currentPhase ? 'bg-blue-600' : phase === currentPhase ? 'bg-blue-400 animate-pulse' : 'bg-gray-200'}`} />
          <span className={`text-[10px] hidden sm:block ${phase <= currentPhase ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>{PHASE_LABELS[phase]}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================
// LOTS PICKER — Session 109 : tantièmes numérateur/dénominateur
// ============================================

interface LotsPickerProps {
  lots: OnboardingLot[];
  totalTantiemes: number;
  onChange: (lots: OnboardingLot[]) => void;
  onChangeTotalTantiemes: (total: number) => void;
  onValidate: () => void;
}

const NATURES_LOT: { value: NatureLot; label: string }[] = [
  { value: 'appartement', label: 'Appartement' }, { value: 'parking', label: 'Parking' },
  { value: 'cave', label: 'Cave' }, { value: 'garage', label: 'Garage' },
  { value: 'commerce', label: 'Commerce' }, { value: 'autre', label: 'Autre' },
];

const LotsPicker: React.FC<LotsPickerProps> = ({ lots, totalTantiemes, onChange, onChangeTotalTantiemes, onValidate }) => {
  const [showHelp, setShowHelp] = useState(false);

  const ajouterLot = () => onChange([...lots, { nature: 'appartement', tantiemes: null }]);
  const supprimerLot = (index: number) => onChange(lots.filter((_, i) => i !== index));
  const modifierNature = (index: number, nature: NatureLot) => { const u = [...lots]; u[index] = { ...u[index], nature }; onChange(u); };
  const modifierTantiemes = (index: number, value: string) => {
    const u = [...lots];
    u[index] = { ...u[index], tantiemes: value === '' ? null : (isNaN(parseInt(value, 10)) ? null : parseInt(value, 10)) };
    onChange(u);
  };

  const totalSaisi = lots.reduce((s, l) => s + (l.tantiemes || 0), 0);
  const pct = totalTantiemes > 0 && totalSaisi > 0 ? ((totalSaisi / totalTantiemes) * 100).toFixed(2) : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <p className="text-sm text-gray-600 font-medium">Vos lots dans la copropriété :</p>

      {/* Total copropriété */}
      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
        <span className="text-xs text-gray-500 whitespace-nowrap">Total copropriété :</span>
        <input type="number" value={totalTantiemes} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v > 0) onChangeTotalTantiemes(v); }} className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500" min={1} />
        <span className="text-xs text-gray-500">èmes</span>
      </div>

      {/* Lots */}
      {lots.map((lot, index) => (
        <div key={index} className="flex items-center gap-2">
          <select value={lot.nature} onChange={(e) => modifierNature(index, e.target.value as NatureLot)} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            {NATURES_LOT.map((n) => (<option key={n.value} value={n.value}>{n.label}</option>))}
          </select>
          <div className="flex items-center gap-1">
            <input type="number" value={lot.tantiemes ?? ''} onChange={(e) => modifierTantiemes(index, e.target.value)} placeholder="Tant." min={1} className="w-20 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500" title="Tantièmes de ce lot" />
            <span className="text-xs text-gray-400 whitespace-nowrap">/ {totalTantiemes.toLocaleString('fr-FR')}</span>
          </div>
          {lots.length > 1 && (<button onClick={() => supprimerLot(index)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" title="Supprimer ce lot"><Trash2 className="w-4 h-4" /></button>)}
        </div>
      ))}

      {/* Total saisi */}
      {totalSaisi > 0 && (
        <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2 text-sm">
          <span className="text-gray-600">Total de vos lots :</span>
          <span className="font-medium text-blue-700">{totalSaisi.toLocaleString('fr-FR')} / {totalTantiemes.toLocaleString('fr-FR')} èmes{pct && <span className="text-gray-500 font-normal ml-1">({pct} %)</span>}</span>
        </div>
      )}

      <button onClick={() => setShowHelp(!showHelp)} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 transition-colors">
        <HelpCircle className="w-3.5 h-3.5" />Je ne connais pas mes tantièmes
      </button>
      {showHelp && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-gray-700 leading-relaxed">
          <p className="font-medium text-blue-700 mb-1">Où trouver vos tantièmes ?</p>
          <p>Vos tantièmes figurent sur votre <span className="font-medium">règlement de copropriété</span> (état descriptif de division) ou sur votre <span className="font-medium">appel de fonds trimestriel</span>. Le total de la copropriété y est aussi indiqué (souvent 1 000, 10 000 ou 100 000). Si vous ne les avez pas sous la main, laissez les champs vides — SENTINELLE vous les redemandera quand vous déposerez l'un de ces documents.</p>
        </div>
      )}

      <button onClick={ajouterLot} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"><Plus className="w-4 h-4" />Ajouter un lot</button>

      <button onClick={onValidate} disabled={lots.length === 0} className="w-full mt-2 bg-blue-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
        <Check className="w-4 h-4" />Valider mes lots
      </button>
    </div>
  );
};

// ============================================
// ADRESSE POSTALE — Session 109
// ============================================

const AdressePostalePicker: React.FC<{ onValidate: (adresse: AdressePostale) => void }> = ({ onValidate }) => {
  const [rue, setRue] = useState('');
  const [codePostal, setCodePostal] = useState('');
  const [ville, setVille] = useState('');
  const isValid = rue.trim().length > 0 && codePostal.trim().length >= 4 && ville.trim().length > 0;
  const handleValider = () => { if (isValid) onValidate({ rue: rue.trim(), codePostal: codePostal.trim(), ville: ville.trim() }); };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2 mb-1"><MapPin className="w-4 h-4 text-gray-500" /><p className="text-sm text-gray-600 font-medium">Votre adresse postale</p></div>
      <input type="text" value={rue} onChange={(e) => setRue(e.target.value)} placeholder="Numéro et rue..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" autoFocus />
      <div className="grid grid-cols-3 gap-2">
        <input type="text" value={codePostal} onChange={(e) => setCodePostal(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="Code postal" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        <input type="text" value={ville} onChange={(e) => setVille(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleValider()} placeholder="Ville" className="col-span-2 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
      </div>
      <button onClick={handleValider} disabled={!isValid} className="w-full bg-blue-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"><Check className="w-4 h-4" />Valider</button>
    </div>
  );
};

// ============================================
// INFOS COPROPRIÉTÉ — Session 159
// ============================================

interface InfosCoproprietePickerProps {
  initialNom?: string;
  initialAdresse?: string;
  onValidate: (infos: InfosCopropriete) => void;
}

const InfosCoproprietePicker: React.FC<InfosCoproprietePickerProps> = ({ initialNom, initialAdresse, onValidate }) => {
  const [nomCopropriete, setNomCopropriete] = useState(initialNom || '');
  const [adresseCopropriete, setAdresseCopropriete] = useState(initialAdresse || '');
  const [codePostal, setCodePostal] = useState('');
  const [commune, setCommune] = useState('');
  const [nombreLotsTotal, setNombreLotsTotal] = useState<string>('');
  const [numeroRNC, setNumeroRNC] = useState('');
  const [showAideRNC, setShowAideRNC] = useState(false);

  const isValid = nomCopropriete.trim().length > 0 &&
    adresseCopropriete.trim().length > 0 &&
    codePostal.trim().length === 5 &&
    commune.trim().length > 0;

  const handleValider = () => {
    if (!isValid) return;
    const result: InfosCopropriete = {
      nomCopropriete: nomCopropriete.trim(),
      adresseCopropriete: adresseCopropriete.trim(),
      codePostal: codePostal.trim(),
      commune: commune.trim().toUpperCase(),
    };
    // Session 159 : N'inclure que les champs optionnels s'ils ont une valeur
    // Firestore refuse undefined, et null sur un champ optionnel est inutile
    const nbLots = nombreLotsTotal ? parseInt(nombreLotsTotal, 10) : 0;
    if (nbLots > 0) {
      result.nombreLotsTotal = nbLots;
    }
    if (numeroRNC.trim()) {
      result.numeroRNC = numeroRNC.trim();
    }
    onValidate(result);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-4 h-4 text-gray-500" />
        <p className="text-sm text-gray-600 font-medium">Votre copropriété</p>
      </div>

      {/* Nom */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Nom de la copropriété</label>
        <input
          type="text"
          value={nomCopropriete}
          onChange={(e) => setNomCopropriete(e.target.value)}
          placeholder="Résidence Les Oliviers..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          autoFocus
        />
      </div>

      {/* Adresse */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Adresse (numéro et rue)</label>
        <input
          type="text"
          value={adresseCopropriete}
          onChange={(e) => setAdresseCopropriete(e.target.value)}
          placeholder="260 rue Camille Delacroix..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Code postal + Commune */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Code postal *</label>
          <input
            type="text"
            value={codePostal}
            onChange={(e) => setCodePostal(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="62155"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Commune *</label>
          <input
            type="text"
            value={commune}
            onChange={(e) => setCommune(e.target.value)}
            placeholder="MERLIMONT"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Nombre de lots total */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Nombre total de lots dans la copropriété (optionnel)</label>
        <input
          type="number"
          value={nombreLotsTotal}
          onChange={(e) => setNombreLotsTotal(e.target.value)}
          placeholder="Ex : 19"
          min={1}
          className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Numéro RNC */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">
          N° d'immatriculation au Registre National des Copropriétés (optionnel)
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={numeroRNC}
            onChange={(e) => setNumeroRNC(e.target.value)}
            placeholder="Ex : AA-0000-0000"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Aide RNC */}
      <button
        onClick={() => setShowAideRNC(!showAideRNC)}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 transition-colors"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        C'est quoi le RNC ?
      </button>
      {showAideRNC && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-gray-700 leading-relaxed">
          <p className="font-medium text-blue-700 mb-1">Registre National des Copropriétés (RNC)</p>
          <p>
            Depuis la loi ALUR, <span className="font-medium">toute copropriété doit être immatriculée</span> au
            registre national. C'est le <span className="font-medium">syndic</span> qui doit s'en charger.
            Le numéro d'immatriculation figure sur la <span className="font-medium">fiche synthétique de la copropriété</span> que
            le syndic est obligé de vous fournir.
          </p>
          <p className="mt-2">
            Si vous ne l'avez pas, ce n'est pas bloquant — mais SENTINELLE pourra vous aider à le demander au syndic.
            Vous pouvez aussi chercher votre copropriété sur{' '}
            <span className="font-medium text-blue-700">registre-coproprietes.gouv.fr</span>
          </p>
        </div>
      )}

      {/* Bouton valider */}
      <button
        onClick={handleValider}
        disabled={!isValid}
        className="w-full mt-2 bg-blue-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Check className="w-4 h-4" />
        Valider
      </button>

      {!isValid && codePostal.length > 0 && codePostal.length < 5 && (
        <p className="text-xs text-amber-600">Le code postal doit contenir 5 chiffres</p>
      )}
    </div>
  );
};

// ============================================
// DATE MOIS/ANNÉE
// ============================================

const DateMoisAnneePicker: React.FC<{ onSelect: (value: string) => void }> = ({ onSelect }) => {
  const [mois, setMois] = useState<number>(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState<number>(new Date().getFullYear());
  const moisLabels = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const annees: number[] = []; for (let a = new Date().getFullYear(); a >= 2015; a--) annees.push(a);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2 mb-1"><Calendar className="w-4 h-4 text-gray-500" /><p className="text-sm text-gray-600 font-medium">Date de livraison</p></div>
      <div className="grid grid-cols-2 gap-3">
        <select value={mois} onChange={(e) => setMois(Number(e.target.value))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">{moisLabels.map((label, i) => (<option key={i} value={i + 1}>{label}</option>))}</select>
        <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">{annees.map((a) => (<option key={a} value={a}>{a}</option>))}</select>
      </div>
      <button onClick={() => onSelect(`${annee}-${String(mois).padStart(2, '0')}`)} className="w-full bg-blue-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"><Check className="w-4 h-4" />Valider</button>
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const OnboardingSentinelleVigie: React.FC<OnboardingSentinelleVigieProps> = ({
  initialStep = 'accueil', initialData, onComplete, onSkip,
}) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { coproprieteId } = useCopropriete();
  const prenom = userProfile?.prenom || 'vous';

  const [currentStep, setCurrentStep] = useState<OnboardingStepId>(initialStep);
  const [messages, setMessages] = useState<OnboardingMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [showAccueil, setShowAccueil] = useState(initialStep === 'accueil');
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({ dateReponses: null, coproprieteNeuve: null, ...initialData });
  const [lots, setLots] = useState<OnboardingLot[]>(initialData?.lots || [{ nature: 'appartement', tantiemes: null }]);
  const [totalTantiemes, setTotalTantiemes] = useState<number>(initialData?.totalTantiemes || 10000);
  const [texteLibre, setTexteLibre] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const nextId = useRef(0);
  const genId = () => `msg-${nextId.current++}`;

  const scrollToBottom = useCallback(() => { setTimeout(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100); }, []);
  useEffect(() => { scrollToBottom(); }, [messages, isTyping, scrollToBottom]);

  const addSentinelleMessage = useCallback((contenu: string): Promise<void> => {
    return new Promise((resolve) => {
      setShowInput(false); setIsTyping(true);
      const delai = Math.min(2000, Math.max(600, contenu.length * 4));
      setTimeout(() => {
        setIsTyping(false);
        setMessages((prev) => [...prev, { id: genId(), role: 'sentinelle', contenu, timestamp: Date.now() }]);
        setTimeout(() => { setShowInput(true); resolve(); }, 300);
      }, delai);
    });
  }, []);

  const addUserMessage = useCallback((contenu: string) => {
    setShowInput(false);
    setMessages((prev) => [...prev, { id: genId(), role: 'utilisateur', contenu, timestamp: Date.now() }]);
  }, []);

  const saveProgress = useCallback(async (step: OnboardingStepId, data: Partial<OnboardingData>) => {
    if (!coproprieteId) return;
    try { await saveOnboardingProgress(coproprieteId, step, data); } catch (err) { console.error('[Onboarding] Erreur sauvegarde:', err); }
  }, [coproprieteId]);

  // ============================================
  // MOTEUR DE CONVERSATION
  // ============================================

  const lancerEtape = useCallback(async (step: OnboardingStepId) => {
    setCurrentStep(step);
    switch (step) {
      case 'accueil': break;

      case 'infos_copropriete':
        await addSentinelleMessage(`Commençons par bien identifier votre copropriété.\n\nLes informations que vous avez saisies à l'inscription sont pré-remplies. Vérifiez-les et complétez ce qui manque — le code postal et la commune sont indispensables.\n\nSi vous connaissez le numéro d'immatriculation au Registre National des Copropriétés (RNC), c'est un vrai plus. Sinon, ne vous inquiétez pas, on le retrouvera ensemble.`);
        break;

      case 'type_copro':
        await addSentinelleMessage(`Première question : votre copropriété est-elle récente ou ancienne ?\n\nC'est important car les copropriétés de moins de 10 ans bénéficient de garanties constructeur puissantes — mais avec des dates d'expiration strictes.`);
        break;

      case 'date_livraison':
        await addSentinelleMessage(`Une copropriété récente, c'est à la fois une chance et un risque. Vous bénéficiez de garanties constructeur puissantes — mais elles ont des dates d'expiration strictes. Chaque jour compte.\n\nQuand votre résidence a-t-elle été livrée ?`);
        break;

      case 'garanties_calculees': {
        const garanties = onboardingData.garanties;
        if (!garanties) { await lancerEtape('nom_syndic'); return; }
        const joursGPA = joursRestantsGarantie(garanties.gpa);
        const joursBiennale = joursRestantsGarantie(garanties.biennale);
        let alerteGPA = '';
        if (joursGPA <= 0) alerteGPA = `\n\nATTENTION : Votre garantie de parfait achèvement (GPA) est expirée depuis ${Math.abs(joursGPA)} jours. Si vous avez des réserves non levées, il faut agir immédiatement.`;
        else if (joursGPA <= 180) alerteGPA = `\n\nALERTE : Il ne reste que ${joursGPA} jours avant l'expiration de votre GPA. C'est la garantie la plus urgente.`;
        let alerteBiennale = '';
        if (joursBiennale <= 365 && joursBiennale > 0) alerteBiennale = `\n\nVotre garantie biennale expire dans ${joursBiennale} jours — elle couvre les équipements (volets, chauffage, plomberie...).`;
        await addSentinelleMessage(`Noté. Voici vos garanties constructeur :\n\nParfait achèvement (GPA) : expire le ${formatDateGarantie(garanties.gpa)}\nGarantie biennale : expire le ${formatDateGarantie(garanties.biennale)}\nGarantie décennale : expire le ${formatDateGarantie(garanties.decennale)}${alerteGPA}${alerteBiennale}\n\nJe surveillerai ces échéances pour vous et vous alerterai en temps voulu.`);
        break;
      }

      case 'nom_syndic':
        await addSentinelleMessage(`Qui est votre syndic de copropriété ?\n\nSi vous ne connaissez pas le nom exact, indiquez ce que vous savez — je m'adapterai.`);
        break;

      case 'relation_syndic':
        await addSentinelleMessage(`Comment décririez-vous votre relation avec ${onboardingData.nomSyndic || 'votre syndic'} ?`);
        break;

      case 'nom_promoteur':
        await addSentinelleMessage(`Qui est le promoteur qui a construit votre résidence ?\n\nC'est lui qui est responsable des malfaçons et des garanties constructeur.`);
        break;

      case 'cs_actif':
        await addSentinelleMessage(`Existe-t-il un conseil syndical actif dans votre copropriété ?`);
        break;

      case 'lots':
        await addSentinelleMessage(`Quels lots possédez-vous dans la copropriété ?\n\nIndiquez la nature de chaque lot et ses tantièmes si vous les connaissez. Les tantièmes déterminent votre quote-part de charges : sans eux, impossible de vérifier si vos appels de fonds sont corrects.\n\nLe total tantièmes est pré-rempli à 10 000 (le plus courant). Modifiez-le si votre copropriété utilise un autre total.`);
        break;

      case 'statut_occupation': {
        const nbLots = onboardingData.lots?.length || 1;
        const msg = nbLots > 1 ? `Vous possédez ${nbLots} lots. Quelle est votre situation d'occupation ?` : `Habitez-vous dans votre lot ou le louez-vous ?`;
        await addSentinelleMessage(`${msg}\n\nC'est important : un propriétaire-bailleur a des préoccupations différentes (charges récupérables, assurance PNO, régularisation locative...). SENTINELLE adaptera ses conseils en conséquence.`);
        break;
      }

      case 'adresse_postale':
        await addSentinelleMessage(`Quelle est votre adresse postale ?\n\nSENTINELLE en a besoin pour rédiger vos courriers recommandés avec un en-tête correct — c'est la base de la méthode SCUD.`);
        break;

      case 'malfacons':
        await addSentinelleMessage(`Avez-vous constaté des malfaçons ou des désordres dans les parties communes ou dans votre lot ?\n\nFissures, infiltrations, équipements défaillants, finitions bâclées... même si ça vous semble mineur, ça compte.`);
        break;

      case 'localisation_malfacons':
        await addSentinelleMessage(`Ces problèmes concernent...\n\nC'est crucial car la procédure est différente :\n- Parties communes : c'est le syndic qui doit agir auprès du promoteur\n- Votre lot : vous agissez directement contre le promoteur\n- Les deux : double front, SENTINELLE vous guidera sur chaque volet`);
        break;

      case 'synthese': {
        const d = onboardingData;
        let s = `Voici ce que j'ai compris de votre situation :\n\n`;
        // Session 159 : Infos copropriété
        if (d.infosCopropriete) {
          s += `Copropriété : ${d.infosCopropriete.nomCopropriete}`;
          s += `\nAdresse : ${d.infosCopropriete.adresseCopropriete}, ${d.infosCopropriete.codePostal} ${d.infosCopropriete.commune}`;
          if (d.infosCopropriete.nombreLotsTotal) s += ` (${d.infosCopropriete.nombreLotsTotal} lots)`;
          if (d.infosCopropriete.numeroRNC) s += `\nRNC : ${d.infosCopropriete.numeroRNC}`;
          s += '\n';
        }
        if (d.coproprieteNeuve) {
          s += `Copropriété récente`;
          if (d.dateLivraison) { const [a, m] = d.dateLivraison.split('-'); const moisNoms = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']; s += `, livrée en ${moisNoms[parseInt(m)]} ${a}`; }
          s += '\n';
        } else { s += 'Copropriété ancienne (plus de 10 ans)\n'; }
        if (d.nomSyndic) {
          s += `Syndic : ${d.nomSyndic}`;
          if (d.relationSyndic) { const rl: Record<RelationSyndic, string> = { ok: ' (relation correcte)', lent: ' (lent / peu réactif)', problemes: ' (relation problématique)', nouveau: ' (nouveau, pas encore d\'avis)' }; s += rl[d.relationSyndic]; }
          s += '\n';
        }
        if (d.nomPromoteur) s += `Promoteur : ${d.nomPromoteur}\n`;
        if (d.csActif) { const cl: Record<EtatCS, string> = { actif: 'Conseil syndical actif', inactif: 'Conseil syndical peu actif', absent: 'Pas de conseil syndical', inconnu: 'Conseil syndical — situation inconnue' }; s += `${cl[d.csActif]}\n`; }
        if (d.lots && d.lots.length > 0) {
          const total = d.totalTantiemes || totalTantiemes;
          const lotsDesc = d.lots.map((l) => l.tantiemes ? `${l.nature} (${l.tantiemes}/${total.toLocaleString('fr-FR')} èmes)` : l.nature).join(', ');
          const totalSaisi = d.lots.reduce((sum, l) => sum + (l.tantiemes || 0), 0);
          s += `Vos lots : ${lotsDesc}`;
          if (totalSaisi > 0) s += ` — ${((totalSaisi / total) * 100).toFixed(2)} % des charges`;
          s += '\n';
        }
        if (d.statutOccupation) { const ol: Record<StatutOccupation, string> = { occupant: 'Propriétaire occupant', bailleur: 'Propriétaire bailleur', mixte: 'Mixte (occupant + bailleur)' }; s += `${ol[d.statutOccupation]}\n`; }
        if (d.adressePostale) s += `Adresse : ${d.adressePostale.rue}, ${d.adressePostale.codePostal} ${d.adressePostale.ville}\n`;
        if (d.malfacons === true) {
          s += `Malfaçons signalées`;
          if (d.localisationMalfacons) { const ll: Record<LocalisationMalfacons, string> = { parties_communes: ' (parties communes)', lot_privatif: ' (lot privatif)', les_deux: ' (parties communes + lot privatif)' }; s += ll[d.localisationMalfacons]; }
          s += '\n';
        } else if (d.malfacons === false) { s += `Pas de malfaçon constatée\n`; }
        if (d.garanties) { const j = joursRestantsGarantie(d.garanties.gpa); s += j > 0 ? `\nGPA : ${j} jours restants` : `\nGPA : expirée`; }
        s += `\n\nEst-ce correct ?`;
        await addSentinelleMessage(s);
        break;
      }

      case 'premier_document': {
        const d = onboardingData;
        if (d.coproprieteNeuve) {
          await addSentinelleMessage(`Avez-vous un document sous la main ? Si oui, déposez-le — je l'analyse immédiatement.\n\nSi vous n'avez rien pour l'instant, pas de souci. Voici les documents que je vous recommande de rassembler en priorité — par ordre d'urgence :\n\n1. PV de réception des parties communes\n   (déclenche vos garanties — demandez-le au syndic si vous ne l'avez pas)\n\n2. PV de livraison de votre lot\n   (avec la liste des réserves que vous avez posées)\n\n3. Dernier appel de fonds trimestriel\n\n4. PV de la dernière assemblée générale\n\n5. Règlement de copropriété\n\nDès que vous en avez un, revenez ici et déposez-le. Je ferai le reste.`);
        } else {
          await addSentinelleMessage(`Avez-vous un document sous la main ? Si oui, déposez-le — je l'analyse immédiatement.\n\nSi vous n'avez rien pour l'instant, pas de souci. Voici les documents utiles à rassembler en priorité :\n\n1. PV de la dernière assemblée générale\n   (pour vérifier que les résolutions votées sont exécutées)\n\n2. Dernier appel de fonds trimestriel\n   (pour vérifier la cohérence avec vos tantièmes)\n\n3. Règlement de copropriété\n\n4. Contrat de syndic\n\nDès que vous en avez un, revenez ici et déposez-le. Je ferai le reste.`);
        }
        break;
      }

      case 'plan_action': {
        const d = onboardingData;
        let p = `${prenom}, votre espace VIGIE est configuré. Voici ce que je vais faire pour vous à partir de maintenant :\n\nACTIF IMMÉDIATEMENT\n- Chaque document que vous déposez sera analysé et classé automatiquement\n`;
        if (d.coproprieteNeuve && d.garanties) p += `- Vos garanties constructeur sont suivies — je vous alerterai avant chaque échéance\n`;
        p += `- Je suis disponible 24h/24 pour rédiger vos courriers avec la méthode SCUD (ton diplomatique, protection juridique)\n\nVOS PROCHAINES ACTIONS\n`;
        if (d.coproprieteNeuve) {
          p += `1. Récupérer le PV de réception — le déposer ici\n`;
          if (d.malfacons) p += `2. Lister vos malfaçons (avec photos si possible)\n`;
          p += `${d.malfacons ? '3' : '2'}. Demander au syndic votre situation comptable individuelle\n`;
        } else { p += `1. Déposer le PV de la dernière AG\n2. Demander au syndic votre situation comptable individuelle\n`; }
        p += `\nJE VOUS ALERTERAI\n`;
        if (d.coproprieteNeuve && d.garanties) p += `- 4 mois avant l'expiration de chaque garantie\n`;
        p += `- Si un montant appelé semble incohérent avec vos tantièmes\n- Si une résolution votée en AG n'est pas exécutée dans les délais\n\nUne question ? Je suis là. Déposez un document, décrivez une situation, ou demandez-moi n'importe quoi sur votre copropriété.`;
        await addSentinelleMessage(p);
        break;
      }

      default: break;
    }
  }, [addSentinelleMessage, onboardingData, prenom, totalTantiemes]);

  // ============================================
  // LANCEMENT INITIAL
  // ============================================

  const hasLaunched = useRef(false);
  useEffect(() => {
    if (hasLaunched.current) return;
    hasLaunched.current = true;
    if (initialStep !== 'accueil') { setShowAccueil(false); lancerEtape(initialStep); }
  }, [initialStep, lancerEtape]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleAccueilCestParti = async () => { setShowAccueil(false); await new Promise((r) => setTimeout(r, 300)); await saveProgress('infos_copropriete', onboardingData); await lancerEtape('infos_copropriete'); };

  // Session 159 : Handler infos copropriété
  const handleInfosCopropriete = async (infos: InfosCopropriete) => {
    addUserMessage(`${infos.nomCopropriete} — ${infos.adresseCopropriete}, ${infos.codePostal} ${infos.commune}${infos.nombreLotsTotal ? ` (${infos.nombreLotsTotal} lots)` : ''}${infos.numeroRNC ? ` — RNC : ${infos.numeroRNC}` : ''}`);
    const u = { ...onboardingData, infosCopropriete: infos };
    setOnboardingData(u);
    await saveProgress('type_copro', u);
    await lancerEtape('type_copro');
  };

  const handleTypeCopro = async (neuve: boolean) => {
    addUserMessage(neuve ? 'Récente — livrée il y a moins de 10 ans' : 'Ancienne — plus de 10 ans');
    const u = { ...onboardingData, coproprieteNeuve: neuve }; setOnboardingData(u);
    if (neuve) { await saveProgress('date_livraison', u); await lancerEtape('date_livraison'); }
    else { await saveProgress('nom_syndic', u); await lancerEtape('nom_syndic'); }
  };

  const handleDateLivraison = async (dateMoisAnnee: string) => {
    const [a, m] = dateMoisAnnee.split('-');
    const moisNoms = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    addUserMessage(`${moisNoms[parseInt(m)]} ${a}`);
    const garanties = calculerGaranties(dateMoisAnnee);
    const u = { ...onboardingData, dateLivraison: dateMoisAnnee, garanties }; setOnboardingData(u);
    await saveProgress('garanties_calculees', u); await lancerEtape('garanties_calculees');
  };

  const handleGarantiesVu = async () => { addUserMessage('Compris, merci'); await saveProgress('nom_syndic', onboardingData); await lancerEtape('nom_syndic'); };

  const handleNomSyndic = async () => { const nom = texteLibre.trim(); if (!nom) return; addUserMessage(nom); setTexteLibre(''); const u = { ...onboardingData, nomSyndic: nom }; setOnboardingData(u); await saveProgress('relation_syndic', u); await lancerEtape('relation_syndic'); };

  const handleRelationSyndic = async (relation: RelationSyndic) => {
    const labels: Record<RelationSyndic, string> = { ok: 'Ça se passe bien', lent: 'Lent, peu réactif', problemes: 'Relation difficile', nouveau: 'Nouveau syndic, pas encore d\'avis' };
    addUserMessage(labels[relation]); const u = { ...onboardingData, relationSyndic: relation }; setOnboardingData(u);
    if (onboardingData.coproprieteNeuve) { await saveProgress('nom_promoteur', u); await lancerEtape('nom_promoteur'); }
    else { await saveProgress('cs_actif', u); await lancerEtape('cs_actif'); }
  };

  const handleNomPromoteur = async () => { const nom = texteLibre.trim(); if (!nom) return; addUserMessage(nom); setTexteLibre(''); const u = { ...onboardingData, nomPromoteur: nom }; setOnboardingData(u); await saveProgress('cs_actif', u); await lancerEtape('cs_actif'); };

  const handleCSActif = async (etat: EtatCS) => {
    const labels: Record<EtatCS, string> = { actif: 'Oui, il est actif', inactif: 'Il existe mais ne fait pas grand-chose', absent: 'Il n\'y a pas de conseil syndical', inconnu: 'Je ne sais pas' };
    addUserMessage(labels[etat]); const u = { ...onboardingData, csActif: etat }; setOnboardingData(u); await saveProgress('lots', u); await lancerEtape('lots');
  };

  const handleLotsValides = async () => {
    const total = totalTantiemes;
    const desc = lots.map((l) => l.tantiemes ? `${l.nature} (${l.tantiemes}/${total.toLocaleString('fr-FR')})` : l.nature).join(', ');
    addUserMessage(`Mes lots : ${desc}`);
    const u = { ...onboardingData, lots: [...lots], totalTantiemes: total }; setOnboardingData(u);
    await saveProgress('statut_occupation', u); await lancerEtape('statut_occupation');
  };

  const handleStatutOccupation = async (statut: StatutOccupation) => {
    const labels: Record<StatutOccupation, string> = { occupant: 'J\'habite dans mon lot', bailleur: 'Je le loue', mixte: 'Mixte — j\'occupe un lot et j\'en loue un autre' };
    addUserMessage(labels[statut]); const u = { ...onboardingData, statutOccupation: statut }; setOnboardingData(u);
    await saveProgress('adresse_postale', u); await lancerEtape('adresse_postale');
  };

  const handleAdressePostale = async (adresse: AdressePostale) => {
    addUserMessage(`${adresse.rue}, ${adresse.codePostal} ${adresse.ville}`);
    const u = { ...onboardingData, adressePostale: adresse }; setOnboardingData(u);
    if (onboardingData.coproprieteNeuve) { await saveProgress('malfacons', u); await lancerEtape('malfacons'); }
    else { await saveProgress('synthese', u); await lancerEtape('synthese'); }
  };

  const handleMalfacons = async (oui: boolean) => {
    addUserMessage(oui ? 'Oui, j\'ai constaté des problèmes' : 'Non, rien à signaler pour l\'instant');
    const u = { ...onboardingData, malfacons: oui }; setOnboardingData(u);
    if (oui) { await saveProgress('localisation_malfacons', u); await lancerEtape('localisation_malfacons'); }
    else { await saveProgress('synthese', u); await lancerEtape('synthese'); }
  };

  const handleLocalisationMalfacons = async (loc: LocalisationMalfacons) => {
    const labels: Record<LocalisationMalfacons, string> = { parties_communes: 'Les parties communes', lot_privatif: 'Mon lot', les_deux: 'Les deux' };
    addUserMessage(labels[loc]); const u = { ...onboardingData, localisationMalfacons: loc }; setOnboardingData(u);
    await saveProgress('synthese', u); await lancerEtape('synthese');
  };

  const handleSyntheseOK = async () => { addUserMessage("C'est exact"); await saveProgress('premier_document', onboardingData); await lancerEtape('premier_document'); };

  const handleSyntheseCorrection = async () => {
    addUserMessage('Je voudrais corriger quelque chose');
    await addSentinelleMessage('Pas de problème, on reprend depuis le début. Vos réponses précédentes sont pré-remplies, vous pouvez les modifier au passage.');
    await lancerEtape('type_copro');
  };

  const handlePasDeDocument = async () => { addUserMessage('Je n\'ai pas de document sous la main'); await saveProgress('plan_action', onboardingData); await lancerEtape('plan_action'); };

  const handleDeposerDocument = async () => {
    addUserMessage('Je vais déposer un document');
    if (coproprieteId) await completeOnboarding(coproprieteId, onboardingData);
    onComplete();
  };

  const handleTerminer = async () => {
    addUserMessage('Merci SENTINELLE, c\'est clair');
    if (coproprieteId) await completeOnboarding(coproprieteId, onboardingData);
    onComplete();
  };

  // ============================================
  // RENDU DES INPUTS
  // ============================================

  const renderInput = () => {
    if (!showInput) return null;
    switch (currentStep) {
      // Session 159 : Nouvelle étape infos copropriété
      case 'infos_copropriete':
        return (
          <InfosCoproprietePicker
            initialNom={userProfile?.vigieInfo?.nomCopropriete || ''}
            initialAdresse={userProfile?.vigieInfo?.adresse || ''}
            onValidate={handleInfosCopropriete}
          />
        );

      case 'type_copro':
        return (<div className="space-y-2">
          <button onClick={() => handleTypeCopro(true)} className="w-full bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 text-sm text-left hover:border-blue-500 hover:bg-blue-100 transition-colors"><span className="font-medium text-gray-800">Récente</span><span className="block text-gray-500 text-xs mt-0.5">Livrée il y a moins de 10 ans</span></button>
          <button onClick={() => handleTypeCopro(false)} className="w-full bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 text-sm text-left hover:border-blue-500 hover:bg-blue-100 transition-colors"><span className="font-medium text-gray-800">Ancienne</span><span className="block text-gray-500 text-xs mt-0.5">Plus de 10 ans</span></button>
        </div>);

      case 'date_livraison': return <DateMoisAnneePicker onSelect={handleDateLivraison} />;

      case 'garanties_calculees':
        return (<button onClick={handleGarantiesVu} className="w-full bg-blue-600 text-white rounded-xl px-4 py-3 text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">Compris, on continue<ChevronRight className="w-4 h-4" /></button>);

      case 'nom_syndic':
        return (<div className="flex gap-2">
          <input type="text" value={texteLibre} onChange={(e) => setTexteLibre(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleNomSyndic()} placeholder="Nom du syndic..." className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" autoFocus />
          <button onClick={handleNomSyndic} disabled={!texteLibre.trim()} className="bg-blue-600 text-white rounded-xl px-4 py-3 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="w-4 h-4" /></button>
        </div>);

      case 'relation_syndic':
        return (<div className="grid grid-cols-2 gap-2">
          {([{ value: 'ok' as RelationSyndic, label: 'Ça se passe bien' }, { value: 'lent' as RelationSyndic, label: 'Lent, peu réactif' }, { value: 'problemes' as RelationSyndic, label: 'Relation difficile' }, { value: 'nouveau' as RelationSyndic, label: "Nouveau, pas d'avis" }] as const).map((opt) => (
            <button key={opt.value} onClick={() => handleRelationSyndic(opt.value)} className="bg-blue-50 border-2 border-blue-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 hover:border-blue-500 hover:bg-blue-100 transition-colors">{opt.label}</button>
          ))}
        </div>);

      case 'nom_promoteur':
        return (<div className="flex gap-2">
          <input type="text" value={texteLibre} onChange={(e) => setTexteLibre(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleNomPromoteur()} placeholder="Nom du promoteur..." className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" autoFocus />
          <button onClick={handleNomPromoteur} disabled={!texteLibre.trim()} className="bg-blue-600 text-white rounded-xl px-4 py-3 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight className="w-4 h-4" /></button>
        </div>);

      case 'cs_actif':
        return (<div className="space-y-2">
          {([{ value: 'actif' as EtatCS, label: 'Oui, il est actif' }, { value: 'inactif' as EtatCS, label: 'Il existe mais ne fait pas grand-chose' }, { value: 'absent' as EtatCS, label: "Il n'y a pas de conseil syndical" }, { value: 'inconnu' as EtatCS, label: 'Je ne sais pas' }] as const).map((opt) => (
            <button key={opt.value} onClick={() => handleCSActif(opt.value)} className="w-full bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 text-sm text-left text-gray-800 hover:border-blue-500 hover:bg-blue-100 transition-colors">{opt.label}</button>
          ))}
        </div>);

      case 'lots':
        return <LotsPicker lots={lots} totalTantiemes={totalTantiemes} onChange={setLots} onChangeTotalTantiemes={setTotalTantiemes} onValidate={handleLotsValides} />;

      case 'statut_occupation':
        return (<div className="space-y-2">
          {([{ value: 'occupant' as StatutOccupation, label: "J'habite dans mon lot", desc: 'Propriétaire occupant' }, { value: 'bailleur' as StatutOccupation, label: 'Je le loue', desc: 'Propriétaire bailleur' }, { value: 'mixte' as StatutOccupation, label: 'Mixte', desc: "J'occupe un lot et j'en loue un autre" }] as const).map((opt) => (
            <button key={opt.value} onClick={() => handleStatutOccupation(opt.value)} className="w-full bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 text-sm text-left hover:border-blue-500 hover:bg-blue-100 transition-colors">
              <span className="font-medium text-gray-800">{opt.label}</span><span className="block text-gray-500 text-xs mt-0.5">{opt.desc}</span>
            </button>
          ))}
        </div>);

      case 'adresse_postale': return <AdressePostalePicker onValidate={handleAdressePostale} />;

      case 'malfacons':
        return (<div className="space-y-2">
          <button onClick={() => handleMalfacons(true)} className="w-full bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 text-sm text-left text-gray-800 hover:border-blue-500 hover:bg-blue-100 transition-colors"><span className="font-medium">Oui, j'ai constaté des problèmes</span></button>
          <button onClick={() => handleMalfacons(false)} className="w-full bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 text-sm text-left text-gray-800 hover:border-blue-500 hover:bg-blue-100 transition-colors"><span className="font-medium">Non, rien à signaler pour l'instant</span></button>
        </div>);

      case 'localisation_malfacons':
        return (<div className="space-y-2">
          {([{ value: 'parties_communes' as LocalisationMalfacons, label: 'Les parties communes', desc: 'Halls, façades, toiture, espaces verts...' }, { value: 'lot_privatif' as LocalisationMalfacons, label: 'Mon lot', desc: "À l'intérieur de mon appartement / local" }, { value: 'les_deux' as LocalisationMalfacons, label: 'Les deux', desc: 'Parties communes et mon lot' }] as const).map((opt) => (
            <button key={opt.value} onClick={() => handleLocalisationMalfacons(opt.value)} className="w-full bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 text-sm text-left hover:border-blue-500 hover:bg-blue-100 transition-colors">
              <span className="font-medium text-gray-800">{opt.label}</span><span className="block text-gray-500 text-xs mt-0.5">{opt.desc}</span>
            </button>
          ))}
        </div>);

      case 'synthese':
        return (<div className="space-y-2">
          <button onClick={handleSyntheseOK} className="w-full bg-blue-600 text-white rounded-xl px-4 py-3 text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"><Check className="w-4 h-4" />C'est exact</button>
          <button onClick={handleSyntheseCorrection} className="w-full bg-gray-100 text-gray-600 rounded-xl px-4 py-3 text-sm hover:bg-gray-200 transition-colors">Je voudrais corriger quelque chose</button>
        </div>);

      case 'premier_document':
        return (<div className="space-y-2">
          <button onClick={handleDeposerDocument} className="w-full bg-blue-600 text-white rounded-xl px-4 py-3 text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"><Upload className="w-4 h-4" />J'ai un document, allons-y</button>
          <button onClick={handlePasDeDocument} className="w-full bg-gray-100 text-gray-600 rounded-xl px-4 py-3 text-sm hover:bg-gray-200 transition-colors">Pas maintenant, je verrai plus tard</button>
        </div>);

      case 'plan_action':
        return (<button onClick={handleTerminer} className="w-full bg-blue-600 text-white rounded-xl px-4 py-3 text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"><Check className="w-4 h-4" />Merci SENTINELLE, c'est clair</button>);

      default: return null;
    }
  };

  // ============================================
  // RENDU PRINCIPAL
  // ============================================

  const currentPhase = STEP_TO_PHASE[currentStep] || 1;

  if (showAccueil) return <AccueilScreen prenom={prenom} onStart={handleAccueilCestParti} onSkip={onSkip} />;

  return (
    <div className="flex flex-col h-full bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
      <ProgressBar currentPhase={currentPhase} />
      <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(100vh - 280px)' }}>
        {messages.map((msg, index) => (<MessageBubble key={msg.id} message={msg} isLatest={index === messages.length - 1} />))}
        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>
      <div className="border-t border-gray-200 bg-white p-4">{renderInput()}</div>
    </div>
  );
};

export default OnboardingSentinelleVigie;
